import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { createPdfExport } from './brandLinksApi'
import { BrandKitBlockPicker, BrandKitChannelPicker } from './BrandKitBlockPicker'
import { DEFAULT_BRAND_KIT_BLOCKS, type BrandKitBlockId } from './brandKitBlocks'
import { ALL_PLATFORMS } from './sections/platformDefault'
import type { ChannelSubs, Platform } from './creatorsApi'

// Export PDF — 수신 브랜드명(선택)을 받아 기록을 남기고 인쇄 탭을 연다 (spec §5-4).
// window.open은 클릭 핸들러에서 동기로 먼저 확보 — API 왕복 후 open은 팝업 차단에 걸린다.
export function ExportPdfModal({
  open,
  creatorId,
  onClose,
  channels,
}: {
  open: boolean
  creatorId: string
  onClose: () => void
  /** 채널 선택지 — 크리에이터가 가진 채널만 고를 수 있게 한다. */
  channels?: ChannelSubs[]
}) {
  const [brandName, setBrandName] = useState('')
  const [blocks, setBlocks] = useState<BrandKitBlockId[]>(DEFAULT_BRAND_KIT_BLOCKS)
  const [platforms, setPlatforms] = useState<Platform[]>(ALL_PLATFORMS)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setBrandName('')
      setBlocks(DEFAULT_BRAND_KIT_BLOCKS)
      setPlatforms(ALL_PLATFORMS)
      setError(null)
    }
  }, [open])

  if (!open) return null

  const submit = async () => {
    // 수신 브랜드는 문서 푸터·워터마크에 박제된다 — 빈 값으로 뽑으면 누구에게 보낸 문서인지
    // 사후에 알 수 없다. 팝업에서 막는다.
    if (!brandName.trim()) {
      setError('수신 브랜드를 입력하세요.')
      return
    }
    const tab = window.open('', '_blank')
    setBusy(true)
    setError(null)
    try {
      const created = await createPdfExport(creatorId, brandName.trim(), blocks, platforms)
      const url = new URL(created.fileUrl, window.location.origin).href
      if (tab) tab.location.replace(url)
      else window.open(url, '_blank', 'noopener')
      onClose()
    } catch (e) {
      tab?.close()
      setError(e instanceof Error ? e.message : 'PDF 생성에 실패했습니다.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-800/40" onClick={onClose}>
      <div
        className="max-h-[86vh] w-[460px] overflow-y-auto border border-line-soft bg-paper p-6"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-lg font-medium text-ink-800">Export PDF</div>
        <p className="mt-1 break-keep font-text text-[12px] leading-[1.5] text-fg-3">
          수신 브랜드와 문서에 담을 내용을 고르세요. 고르지 않은 블록은 문서에서 빠집니다.
        </p>
        <label className="mt-4 block">
          <span className="font-text text-[11px] font-medium tracking-[0.14em] text-fg-3">
            RECIPIENT BRAND
          </span>
          <input
            className="mt-1 h-[38px] w-full border border-line-soft bg-white px-3 font-text text-[13px]"
            placeholder="e.g. espoir"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
          />
        </label>
        <div className="mt-4">
          <BrandKitChannelPicker value={platforms} onChange={setPlatforms} channels={channels} />
        </div>
        <div className="mt-4">
          <BrandKitBlockPicker value={blocks} onChange={setBlocks} />
        </div>
        <p className="mt-3 font-text text-[12px] text-fg-3">
          수신 브랜드는 표지·푸터에 박제됩니다. 내보낸 기록은 이름과 함께 남고 히스토리에서
          다시 받을 수 있습니다.
        </p>
        {error && <p className="mt-2 font-text text-[12px] text-status-danger" role="alert">{error}</p>}
        <div className="mt-5 flex items-center justify-between">
          <Link to="/creators/brand-links?tab=pdf" className="font-text text-[12px] font-medium text-brand-blue">
            View export history →
          </Link>
          <div className="flex items-center gap-2">
            <button className="h-[36px] border border-line-soft bg-white px-4 font-text text-[13px]" onClick={onClose}>
              Cancel
            </button>
            <button
              className="h-[36px] bg-brand-blue px-4 font-text text-[13px] font-medium text-white disabled:opacity-50"
              disabled={busy}
              onClick={() => void submit()}
            >
              {busy ? 'Generating…' : 'Export'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
